import type {ReactNode, Ref} from "react";

import {cn} from "@project/design-system-web";

export interface PageContainerProps {
  children: ReactNode;
  /**
   * If true, removes all internal padding and gaps.
   * Useful for full-bleed pages like maps, dashboards, or completely custom layouts.
   */
  flush?: boolean;
  /**
   * Additional classes appended to the root container.
   * Useful for overriding flex direction (e.g. `lg:flex-row`).
   */
  className?: string;
  ref?: Ref<HTMLDivElement>;
}

/**
 * PageContainer — semantic layout wrapper for root page components.
 *
 * Enforces responsive "safe area" padding and structural gaps across the application.
 * Manages its own `overflow-y-auto` to ensure scrollbars stay glued to the
 * absolute edge of the browser viewport, avoiding "floating scrollbar" syndrome.
 *
 * @example
 * ```tsx
 * export function UsersPage() {
 *   return (
 *     <PageContainer>
 *       <TableToolbar />
 *       <Table />
 *     </PageContainer>
 *   );
 * }
 * ```
 */
export function PageContainer({children, className, flush = false, ref}: PageContainerProps) {
  return (
    <div ref={ref} className={cn("@container", "flex h-full min-h-0 flex-1 flex-col")}>
      <div
        className={cn(
          "flex h-full min-h-0 flex-1 flex-col overflow-y-auto",
          !flush && "gap-2 p-2 @lg:gap-4 @lg:p-4",
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}
